import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Activity,
  Users,
  Eye,
  TrendingDown,
  Clock,
  Info,
  RefreshCw,
  BarChart2,
  Globe,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MEASUREMENT_ID = 'G-TTSNJ350LE';

const TOOLTIP_STYLE = {
  backgroundColor: '#1e293b',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  color: '#f1f5f9',
  fontSize: 12,
  padding: '8px 12px',
};

function formatDuration(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m === 0) return `${rem}s`;
  return `${m}m ${rem}s`;
}

function formatShortDate(gaDate) {
  // GA4 returns dates as "YYYYMMDD"
  if (!gaDate || gaDate.length !== 8) return gaDate;
  const y = gaDate.slice(0, 4);
  const m = gaDate.slice(4, 6);
  const d = gaDate.slice(6, 8);
  const dt = new Date(`${y}-${m}-${d}T00:00:00`);
  return dt.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
}

function formatNumber(n) {
  return new Intl.NumberFormat('es-MX').format(Number(n) || 0);
}

const METRIC_CARDS = [
  {
    id: 'sessions',
    label: 'Sesiones (30 días)',
    icon: Activity,
    key: 'sessions',
    format: formatNumber,
  },
  {
    id: 'users',
    label: 'Usuarios únicos',
    icon: Users,
    key: 'users',
    format: formatNumber,
  },
  {
    id: 'pageviews',
    label: 'Páginas vistas',
    icon: Eye,
    key: 'pageviews',
    format: formatNumber,
  },
  {
    id: 'bounce',
    label: 'Tasa de rebote',
    icon: TrendingDown,
    key: 'bounce_rate',
    format: (v) => `${(Number(v) || 0).toFixed(1)}%`,
  },
  {
    id: 'duration',
    label: 'Duración promedio',
    icon: Clock,
    key: 'avg_session_duration',
    format: formatDuration,
  },
];

export default function Analytics() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      setData({
        error: err?.response?.data?.detail || err?.message || 'Error al cargar datos',
        overview: { sessions: 0, users: 0, pageviews: 0, bounce_rate: 0, avg_session_duration: 0 },
        top_pages: [],
        traffic_sources: [],
        daily_visitors: [],
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      setLoading(true);
      loadData();
    }
  }, [token, loadData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const overview = data?.overview || {
    sessions: 0,
    users: 0,
    pageviews: 0,
    bounce_rate: 0,
    avg_session_duration: 0,
  };
  const topPages = data?.top_pages || [];
  const trafficSources = data?.traffic_sources || [];
  const dailyVisitors = data?.daily_visitors || [];
  const hasError = Boolean(data?.error);
  const hasNoData =
    !hasError &&
    !loading &&
    overview.sessions === 0 &&
    overview.users === 0 &&
    overview.pageviews === 0 &&
    dailyVisitors.length === 0;

  const chartData = dailyVisitors.map((d) => ({
    date: formatShortDate(d.date),
    users: d.users,
  }));

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      {/* Page Header + Refresh */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1
            className="text-2xl font-bold mb-1"
            style={{ color: '#0f172a' }}
            data-testid="analytics-title"
          >
            Analítica Web
          </h1>
          <p className="text-sm text-gray-400" data-testid="analytics-subtitle">
            Estadísticas de visitas de kindtechnologies.net · {data?.period || 'Últimos 30 días'}
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing || loading}
          className="gap-2 bg-blue-600 hover:bg-blue-700 text-white border-0"
          data-testid="analytics-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Actualizando...' : 'Actualizar datos'}
        </Button>
      </div>

      {/* Info Banner (measurement id + empty/error state helper) */}
      {(hasError || hasNoData) && (
        <div
          className="flex items-start gap-3 rounded-lg border border-blue-800 bg-blue-950/40 px-4 py-3"
          data-testid="analytics-info-banner"
          role="status"
        >
          <Info className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-blue-300 leading-relaxed">
            {hasError ? (
              <p>
                No pudimos conectar con Google Analytics. Los datos aparecerán aquí una vez que el
                sitio tenga visitantes. Google Analytics puede tardar 24-48 horas en procesar los
                primeros datos.
              </p>
            ) : (
              <p>
                Los datos aparecerán aquí una vez que el sitio tenga visitantes. Google Analytics
                puede tardar 24-48 horas en procesar los primeros datos.
              </p>
            )}
            <p className="mt-1">
              <span className="text-blue-300">Measurement ID:</span>{' '}
              <span
                className="font-mono font-semibold text-white bg-blue-900/60 px-2 py-0.5 rounded"
                data-testid="analytics-measurement-id"
              >
                {MEASUREMENT_ID}
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Metric Cards */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
        data-testid="analytics-metric-cards"
      >
        {METRIC_CARDS.map((m) => {
          const Icon = m.icon;
          const raw = overview[m.key];
          return (
            <Card
              key={m.id}
              className="border border-gray-700 bg-gray-900"
              data-testid={`analytics-metric-${m.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div
                    className="h-10 w-10 rounded-lg bg-blue-950/60 border border-blue-800 flex items-center justify-center text-blue-400"
                    aria-hidden="true"
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                    {m.label}
                  </p>
                  {loading ? (
                    <Skeleton className="h-8 w-24 bg-gray-800" />
                  ) : (
                    <p
                      className="text-2xl font-bold text-white"
                      data-testid={`analytics-metric-${m.id}-value`}
                    >
                      {m.format(raw)}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Daily Visitors Bar Chart */}
      <Card
        className="border border-gray-700 bg-gray-900"
        data-testid="analytics-daily-visitors-card"
      >
        <CardHeader>
          <CardTitle className="text-base font-semibold text-white flex items-center gap-2">
            <BarChart2 className="h-5 w-5 text-blue-400" aria-hidden="true" />
            Visitantes últimos 7 días
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-64 w-full bg-gray-800" />
          ) : chartData.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
              Aún no hay datos de visitantes para mostrar.
            </div>
          ) : (
            <div className="h-64 w-full" data-testid="analytics-bar-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis
                    dataKey="date"
                    stroke="#94a3b8"
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                    allowDecimals={false}
                  />
                  <RechartsTooltip
                    contentStyle={TOOLTIP_STYLE}
                    cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                  />
                  <Bar dataKey="users" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two-column: Top pages + Traffic sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top pages */}
        <Card
          className="border border-gray-700 bg-gray-900"
          data-testid="analytics-top-pages-card"
        >
          <CardHeader>
            <CardTitle className="text-base font-semibold text-white flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-400" aria-hidden="true" />
              Páginas más visitadas
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full bg-gray-800" />
                ))}
              </div>
            ) : topPages.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">
                Aún no hay datos de páginas visitadas.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700 hover:bg-transparent">
                    <TableHead className="text-gray-300">Página</TableHead>
                    <TableHead className="text-gray-300 text-right">Vistas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topPages.map((p, i) => (
                    <TableRow
                      key={`${p.page}-${i}`}
                      className="border-gray-700 hover:bg-gray-800"
                      data-testid={`analytics-top-page-row-${i}`}
                    >
                      <TableCell className="text-white font-mono text-xs">
                        {p.page || '/'}
                      </TableCell>
                      <TableCell className="text-white text-right font-semibold">
                        {formatNumber(p.views)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Traffic sources */}
        <Card
          className="border border-gray-700 bg-gray-900"
          data-testid="analytics-traffic-sources-card"
        >
          <CardHeader>
            <CardTitle className="text-base font-semibold text-white flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-400" aria-hidden="true" />
              Fuentes de tráfico
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full bg-gray-800" />
                ))}
              </div>
            ) : trafficSources.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">
                Aún no hay datos de fuentes de tráfico.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700 hover:bg-transparent">
                    <TableHead className="text-gray-300">Fuente</TableHead>
                    <TableHead className="text-gray-300 text-right">Sesiones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trafficSources.map((s, i) => (
                    <TableRow
                      key={`${s.source}-${i}`}
                      className="border-gray-700 hover:bg-gray-800"
                      data-testid={`analytics-traffic-source-row-${i}`}
                    >
                      <TableCell className="text-white">{s.source || 'Directo'}</TableCell>
                      <TableCell className="text-white text-right font-semibold">
                        {formatNumber(s.sessions)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
